import { useState } from 'react';
import { clsx } from 'clsx';
import { Plus } from 'lucide-react';
import styles from './chips.module.css';

interface ReasonChipsProps {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Allow typing a reason that is not in the list. */
  allowCustom?: boolean;
  emptyHint?: string;
}

export function ReasonChips({ options, selected, onChange, disabled, allowCustom = true, emptyHint }: ReasonChipsProps) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const all = [...options, ...selected.filter((s) => !options.includes(s))];

  const toggle = (reason: string) =>
    onChange(selected.includes(reason) ? selected.filter((r) => r !== reason) : [...selected, reason]);

  const commit = () => {
    const value = draft.trim();
    if (value && !selected.includes(value)) onChange([...selected, value]);
    setDraft('');
    setAdding(false);
  };

  if (all.length === 0 && emptyHint && !allowCustom) return <p className={styles.empty}>{emptyHint}</p>;

  return (
    <div className={styles.row}>
      {all.map((reason) => (
        <button
          key={reason}
          type="button"
          className={clsx(styles.chip, selected.includes(reason) && styles.chipOn)}
          onClick={() => toggle(reason)}
          disabled={disabled}
          aria-pressed={selected.includes(reason)}
        >
          {reason}
        </button>
      ))}
      {allowCustom && !disabled && (
        adding ? (
          <input
            autoFocus
            className={styles.inlineInput}
            placeholder="Other reason…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              }
              if (e.key === 'Escape') {
                setDraft('');
                setAdding(false);
              }
            }}
            maxLength={80}
          />
        ) : (
          <button type="button" className={styles.addChip} onClick={() => setAdding(true)}>
            <Plus size={12} /> Other
          </button>
        )
      )}
      {all.length === 0 && emptyHint && <p className={styles.empty}>{emptyHint}</p>}
    </div>
  );
}
